import React from 'react';
import { useQuery } from '@tanstack/react-query';
import UserLayout from '../../components/Layout/UserLayout';
import { Card, StatCard, Badge, Button, Spinner, EmptyState } from '../../components/UI';
import { dashboardAPI, leadsAPI, callLogsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineUsers,
  HiOutlinePhone,
  HiOutlineTrendingUp,
  HiOutlineClipboardList,
  HiOutlineRefresh,
  HiOutlineArrowRight,
} from 'react-icons/hi';
import { format } from 'date-fns';

const UserDashboard = () => {
  const navigate = useNavigate();

  // Fetch dashboard stats
  const { data: statsData, isLoading: statsLoading, refetch } = useQuery({
    queryKey: ['userDashboardStats'],
    queryFn: async () => {
      const response = await dashboardAPI.getStats();
      return response.data?.data || {};
    },
  });

  // Fetch leads counts
  const { data: leadsCounts } = useQuery({
    queryKey: ['userLeadsCounts'],
    queryFn: async () => {
      const response = await leadsAPI.getCounts();
      return response.data?.data || {};
    },
  });

  // Fetch recent leads
  const { data: recentLeads, isLoading: leadsLoading } = useQuery({
    queryKey: ['userRecentLeads'],
    queryFn: async () => {
      const response = await leadsAPI.getMy({ limit: 5 });
      return response.data?.data || [];
    },
  });

  const stats = statsData || {};
  const totalLeads = leadsCounts ? Object.values(leadsCounts).reduce((a, b) => a + (b || 0), 0) : 0;

  const handleRefresh = () => {
    refetch();
    toast.success('Dashboard refreshed');
  };

  const isLoading = statsLoading || leadsLoading;

  return (
    <UserLayout>
      <div className="p-4 lg:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Dashboard</h1>
            <p className="text-gray-500 text-sm mt-1">Welcome back! Here's your work overview.</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            icon={<HiOutlineRefresh className="w-4 h-4" />}
            onClick={handleRefresh}
            loading={isLoading}
          >
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="My Leads"
            value={totalLeads}
            icon={<HiOutlineUsers className="w-6 h-6 text-white" />}
            color="primary"
          />
          <StatCard
            title="New Leads"
            value={leadsCounts?.new || 0}
            icon={<HiOutlineClipboardList className="w-6 h-6 text-white" />}
            color="success"
          />
          <StatCard
            title="Follow Ups"
            value={leadsCounts?.follow_up || 0}
            icon={<HiOutlinePhone className="w-6 h-6 text-white" />}
            color="warning"
          />
          <StatCard
            title="Converted"
            value={leadsCounts?.converted || 0}
            icon={<HiOutlineTrendingUp className="w-6 h-6 text-white" />}
            color="info"
          />
        </div>

        {/* Lead Categories Summary */}
        <Card padding="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Lead Status Overview</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/app/leads')}
            >
              View All
              <HiOutlineArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-3 bg-blue-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-blue-600">{leadsCounts?.new || 0}</p>
              <p className="text-xs text-gray-600">New</p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-yellow-600">{leadsCounts?.follow_up || 0}</p>
              <p className="text-xs text-gray-600">Follow Up</p>
            </div>
            <div className="p-3 bg-orange-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-orange-600">{leadsCounts?.warm || 0}</p>
              <p className="text-xs text-gray-600">Warm</p>
            </div>
            <div className="p-3 bg-red-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-red-600">{leadsCounts?.hot || 0}</p>
              <p className="text-xs text-gray-600">Hot</p>
            </div>
          </div>
        </Card>

        {/* Recent Leads */}
        <Card padding="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">My Recent Leads</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/app/leads')}
            >
              View All
              <HiOutlineArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          {leadsLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : recentLeads?.length > 0 ? (
            <div className="space-y-3">
              {recentLeads.slice(0, 5).map((lead) => (
                <div
                  key={lead._id}
                  className="flex items-center justify-between p-3 rounded-xl bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <span className="text-green-600 font-medium text-sm">
                        {lead.name?.charAt(0)?.toUpperCase() || 'L'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{lead.name}</p>
                      <p className="text-sm text-gray-500">{lead.phone}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant={lead.status} size="sm">
                      {lead.status?.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<HiOutlineUsers className="w-8 h-8 text-gray-400" />}
              title="No leads assigned yet"
              description="Leads will appear here when assigned to you"
            />
          )}
        </Card>

        {/* Quick Actions - Mobile */}
        <div className="lg:hidden grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            className="flex-col py-4"
            onClick={() => navigate('/app/leads')}
          >
            <HiOutlineUsers className="w-6 h-6 mb-1" />
            My Leads
          </Button>
          <Button
            variant="outline"
            className="flex-col py-4"
            onClick={() => navigate('/app/call-logs')}
          >
            <HiOutlinePhone className="w-6 h-6 mb-1" />
            Call Logs
          </Button>
        </div>
      </div>
    </UserLayout>
  );
};

export default UserDashboard;